import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram, Youtube, MessageCircle, ArrowRight } from 'lucide-react';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const SOCIAL_ICON_CLASS = 'w-8 h-8 border border-white/20 rounded-lg flex items-center justify-center hover:bg-white/10 hover:border-white/40 transition-colors duration-150';

export default function Footer() {
  const s = useSiteSettings();

  /* ── Strictly no fallbacks — only render if value exists ─────── */
  const email = s.email || null;
  const phone = s.phone || null;
  const mobile = s.mobile || null;
  const whatsapp = s.social_whatsapp || null;
  const address = s.address || null;
  const businessHours = s.business_hours || null;

  const copyrightText = (s.copyright_text || '© {year} PKRUPARTH ENTERPRISE PVT. LTD. All rights reserved.').replace('{year}', new Date().getFullYear());
  const tagline = s.footer_tagline || s.tagline || 'One Company. Two Markets.';
  const companyName = s.company_name || null;

  const hasSocial = s.social_linkedin || s.social_twitter || s.social_facebook || s.social_instagram || s.social_youtube || whatsapp;
  const hasContact = email || phone || mobile || address;

  return (
    <footer className="bg-[#0A1628] text-white">
      {/* CTA Band */}
      <div className="border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <p className="overline-label text-[#D4AF37] mb-3">Ready to get started?</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white leading-tight">
              Let's build something better, together.
            </h2>
          </div>
          <Link
            to="/contact"
            className="flex-shrink-0 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0A1628] font-semibold px-8 py-4 rounded-full transition-colors duration-200 flex items-center gap-2 text-sm"
          >
            Let's Talk <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">

          {/* Brand */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-0 mb-5">
              {s.light_logo_url ? (
                <img src={s.light_logo_url} alt={s.brand_name || 'NKP VENTURES'} className="h-8 w-auto object-contain" />
              ) : (
                <>
                  <span className="font-serif font-bold text-xl text-white tracking-tight">NKP</span>
                  <span className="ml-2 font-mono text-[0.58rem] tracking-[0.32em] uppercase font-medium text-[#D4AF37]">VENTURES</span>
                </>
              )}
            </Link>
            <p className="text-white/55 text-sm leading-relaxed mb-4 font-sans">
              {tagline}
            </p>
            {companyName && (
              <p className="text-white/30 text-xs font-sans mb-6">{companyName}</p>
            )}
            {hasSocial && (
              <div className="flex items-center gap-2 flex-wrap">
                {s.social_linkedin && (
                  <a href={s.social_linkedin} target="_blank" rel="noopener noreferrer" className={SOCIAL_ICON_CLASS} aria-label="LinkedIn">
                    <Linkedin className="w-4 h-4 text-white/70" />
                  </a>
                )}
                {s.social_twitter && (
                  <a href={s.social_twitter} target="_blank" rel="noopener noreferrer" className={SOCIAL_ICON_CLASS} aria-label="Twitter / X">
                    <Twitter className="w-4 h-4 text-white/70" />
                  </a>
                )}
                {s.social_facebook && (
                  <a href={s.social_facebook} target="_blank" rel="noopener noreferrer" className={SOCIAL_ICON_CLASS} aria-label="Facebook">
                    <Facebook className="w-4 h-4 text-white/70" />
                  </a>
                )}
                {s.social_instagram && (
                  <a href={s.social_instagram} target="_blank" rel="noopener noreferrer" className={SOCIAL_ICON_CLASS} aria-label="Instagram">
                    <Instagram className="w-4 h-4 text-white/70" />
                  </a>
                )}
                {s.social_youtube && (
                  <a href={s.social_youtube} target="_blank" rel="noopener noreferrer" className={SOCIAL_ICON_CLASS} aria-label="YouTube">
                    <Youtube className="w-4 h-4 text-white/70" />
                  </a>
                )}
                {whatsapp && (
                  <a href={whatsapp} target="_blank" rel="noopener noreferrer" className={SOCIAL_ICON_CLASS} aria-label="WhatsApp">
                    <MessageCircle className="w-4 h-4 text-white/70" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Solutions */}
          <div className="lg:col-span-3">
            <h4 className="overline-label text-teal-400 mb-5">Solutions</h4>
            <div className="space-y-4 mb-6">
              <div>
                <Link to="/solutions/india" className="text-sm font-semibold text-white/80 hover:text-white transition-colors duration-150 block mb-1">
                  India
                </Link>
                <p className="text-xs text-white/40 font-sans">Digital & Business Solutions</p>
              </div>
              <div>
                <Link to="/solutions/global" className="text-sm font-semibold text-white/80 hover:text-white transition-colors duration-150 block mb-1">
                  Global
                </Link>
                <p className="text-xs text-white/40 font-sans">Outsourcing & Business Solutions</p>
              </div>
            </div>
            <Link to="/solutions" className="text-xs font-semibold text-teal-400 hover:text-teal-300 transition-colors duration-150">
              All Solutions →
            </Link>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h4 className="overline-label text-teal-400 mb-5">Company</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'About Us', href: '/about' },
                { label: 'Industries', href: '/industries' },
                { label: 'Insights', href: '/insights' },
                { label: 'Case Experience', href: '/case-experience' },
                { label: 'Contact', href: '/contact' },
              ].map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-white/55 hover:text-white transition-colors duration-150 font-sans">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact — only if configured */}
          {hasContact && (
            <div className="lg:col-span-3">
              <h4 className="overline-label text-teal-400 mb-5">Contact</h4>
              <ul className="space-y-4">
                {email && (
                  <li className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                    <a href={`mailto:${email}`} className="text-sm text-white/60 hover:text-white transition-colors duration-150 font-sans break-all">
                      {email}
                    </a>
                  </li>
                )}
                {phone && (
                  <li className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                    <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-sm text-white/60 hover:text-white transition-colors duration-150 font-sans">
                      {phone}
                    </a>
                  </li>
                )}
                {mobile && mobile !== phone && (
                  <li className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                    <a href={`tel:${mobile.replace(/\s/g, '')}`} className="text-sm text-white/60 hover:text-white transition-colors duration-150 font-sans">
                      {mobile}
                    </a>
                  </li>
                )}
                {address && (
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-white/60 font-sans leading-relaxed">{address}</span>
                  </li>
                )}
                {businessHours && (
                  <li className="text-sm text-white/50 font-sans leading-relaxed">{businessHours}</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/35 font-sans">{copyrightText}</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-xs text-white/35 hover:text-white/65 transition-colors duration-150 font-sans">Privacy Policy</Link>
            <Link to="/terms" className="text-xs text-white/35 hover:text-white/65 transition-colors duration-150 font-sans">Terms</Link>
            <Link to="/admin/login" data-testid="footer-admin-login" className="text-xs text-white/20 hover:text-white/45 transition-colors duration-150 font-sans">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
